import { REMOVAL_DATE_INDEX, computeRunningTimeFromDates, findAttributeValue } from './moveWizardSync';

/**
 * Date → runningTime sync for the installed-base event wizard (ASSET_REMOVE and
 * any other event type whose TDAs carry installationDate, removalDate and
 * runningTimeHours).
 *
 * The backend seeds runningTimeHours once from the asset location's dates. When
 * the user changes either date, runningTime is re-derived with the backend's
 * formula (whole days × averageRuntimePerDay, see computeRunningTimeFromDates).
 * The date always wins over a manual runningTime edit; a manual edit sticks
 * until the next date change.
 *
 * The two dates usually live on different wizard steps, so the baseline is
 * seeded from the attribute defaults and each step's values slice only updates
 * the dates it actually carries.
 */

export const INSTALLATION_DATE_INDEX = 'installationDate';

export interface EventDates {
    installationDate: string | null;
    removalDate: string | null;
}

interface AttributeLike {
    index?: string | null;
    value?: unknown;
}

// A cleared date ('' from the form) equals null.
const normalizeDate = (value: unknown): string | null =>
    value === null || value === undefined || value === '' ? null : String(value);

/** Baseline dates from the TDA defaults, before the user has touched anything. */
export const getDefaultEventDates = (attrs: AttributeLike[] | null | undefined): EventDates => ({
    installationDate: normalizeDate(findAttributeValue(attrs, INSTALLATION_DATE_INDEX)),
    removalDate: normalizeDate(findAttributeValue(attrs, REMOVAL_DATE_INDEX)),
});

/**
 * Diffs a step's values slice against the known dates. Only date keys present
 * in `next` are considered. Returns the updated dates and, when a date actually
 * changed and both dates are usable, the recomputed runningTime in hours.
 */
export const evaluateEventDatesChange = (
    prev: EventDates,
    next: Record<string, any>,
    averageRuntimePerDay: number | null | undefined
): { dates: EventDates; nextRunningTime?: number } => {
    const dates: EventDates = {
        installationDate:
            INSTALLATION_DATE_INDEX in next ? normalizeDate(next[INSTALLATION_DATE_INDEX]) : prev.installationDate,
        removalDate: REMOVAL_DATE_INDEX in next ? normalizeDate(next[REMOVAL_DATE_INDEX]) : prev.removalDate,
    };

    const changed =
        dates.installationDate !== prev.installationDate || dates.removalDate !== prev.removalDate;
    if (!changed) return { dates };

    const recomputed = computeRunningTimeFromDates(dates.installationDate, dates.removalDate, averageRuntimePerDay);
    return recomputed === null ? { dates } : { dates, nextRunningTime: recomputed };
};
